import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import cn from 'classnames';
import shuffle from 'lodash/shuffle';
import { useEffect, useRef, useState } from 'react';
import Swiper, { Navigation, A11y } from 'swiper';
import 'swiper/css';

import { useExpEditor } from 'src/hooks/useExpEditor';

import { ComponentProps } from 'lib/component-props';
import { Website } from 'lib/component-props/model';

import AnimateInContainer from 'components/BaseHelpers/AnimateInContainer';
import LinkOrComponent from 'components/BaseHelpers/LinkOrComponent/LinkOrComponent';
import ModuleHeader from 'components/BaseHelpers/ModuleHeader';
import RichText from 'components/BaseHelpers/RichText';
import Text from 'components/BaseHelpers/Text';

import Chevron from '../../../assets/icons/chevron-carousel.svg';

import styles from './InsightsClientStoriesCarousel.module.scss';

type ClientStoriesCarousel = Website.Project.Main.ComponentTypes.Insights.ClientStoriesCarousel;

type Story = {
  url: string;
  id: string;
  fields: {
    abstract: Field<string>;
    title: Field<string>;
  };
};

export type InsightsClientStoriesCarouselProps = ComponentProps &
  Omit<ClientStoriesCarousel, 'fields'> & {
    fields?: Omit<NonNullable<ClientStoriesCarousel['fields']>, 'pinnedClientStories'> & {
      pinnedClientStories?: Story[];
      search?: {
        totalFound: number;
        results: Array<Story>;
      };
    };
  };

const InsightsClientStoriesCarousel = (props: InsightsClientStoriesCarouselProps): JSX.Element => {
  const { hasItemsOrIsEE, hasValueOrIsEE } = useExpEditor();

  const { moduleHeader, numberOfStories, pinnedClientStories, search } = props.fields || {};

  const swiper = useRef<Swiper>();

  const parsedNumberOfStories = parseInt(numberOfStories?.value || '');

  const [stories, setStories] = useState<Story[]>([]);

  const hasCarousel = stories && stories?.length > 2;

  Swiper.use([Navigation, A11y]);

  useEffect(() => {
    setStories(
      [...(pinnedClientStories || []), ...shuffle(search?.results || [])].slice(
        0,
        parsedNumberOfStories
      )
    );
  }, []);

  useEffect(() => {
    const initCarousel = () => {
      swiper.current = new Swiper(`.carousel-${props?.rendering?.uid}`, {
        loop: true,
        autoHeight: true,
        breakpoints: {
          768: {
            slidesPerView: 2,
          },
        },
        a11y: {
          prevSlideMessage: 'Previous slide',
          nextSlideMessage: 'Next slide',
        },
        slidesPerView: 1,
        spaceBetween: 48,
        navigation: {
          nextEl: '.next',
          prevEl: '.prev',
        },
      });
    };
    if (!swiper.current && stories.length > 0) {
      initCarousel();
    }
  }, [stories]);

  if (
    !hasValueOrIsEE(moduleHeader) ||
    (!hasItemsOrIsEE(pinnedClientStories) && !hasItemsOrIsEE(search?.results))
  ) {
    return <></>;
  }

  return (
    <AnimateInContainer className={cn(styles.main, 'container-10', 'spacer')}>
      <ModuleHeader variant="double-line" header={moduleHeader} />
      <div className={cn(styles[`swiper-container`])}>
        <div
          className={cn(
            styles[`swiper`],
            { [`swiper`]: hasCarousel },
            `carousel-${props?.rendering?.uid}`
          )}
        >
          <div
            className={cn(styles[`swiper-wrapper`], {
              [`swiper-wrapper`]: hasCarousel,
              [styles[`wrapper`]]: !hasCarousel,
            })}
          >
            {stories?.map((story, index) => {
              return (
                <div
                  key={`client-story-${index}-${props?.rendering?.uid}`}
                  className={cn({ [`swiper-slide`]: hasCarousel })}
                >
                  <LinkOrComponent field={story?.url} className={styles[`card`]}>
                    <Text className={styles[`headline`]} field={story?.fields?.title} />
                    <RichText
                      className={styles[`abstract`]}
                      field={story?.fields?.abstract}
                      replaceAnchorTags={true}
                    />
                  </LinkOrComponent>
                </div>
              );
            })}
          </div>
        </div>
        {hasCarousel && (
          <div className={styles[`navigation`]}>
            <span className={cn(styles[`icon`], `prev`, styles[`prev`])}>
              <Chevron />
            </span>
            <span className={cn(styles[`icon`], `next`, styles[`next`])}>
              <Chevron />
            </span>
          </div>
        )}
      </div>
    </AnimateInContainer>
  );
};

export default InsightsClientStoriesCarousel;
